import { useState, useEffect, useMemo, useCallback } from 'react';
import { useRecordContext, useGetOne, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import type { Variant } from '@src/types';
import type { PartnerSettings } from '@src/settings';
import { getCatalogMediaUploadUrl, uploadToS3 } from '@src/apis/reware';
import { requireAuth, requirePartner } from '@src/providers/dataProvider/shared';
import { MediaAssetCard } from './MediaAssetCard';
import type { FilterDimension } from './scopeKeyUtils';
import { isVideoFile } from './mediaUtils';
import { useMediaState, nextAvailableName } from './useMediaState';

const ALL_VALUE = '__all__';

/** Derive a dropdown label from a key like 'seasonDisplays' → 'Season'. */
const toLabel = (key: string): string => {
  const stripped = key.replace(/Displays$/i, '');
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
};

const MediaTab = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const variants: Variant[] = record?.variants ?? [];

  // Get partner settings for blended photography configuration
  const { data: settings } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });
  const blendedPhotographyImages = settings?.blendedPhotographyImages ?? [];
  const blendedPhotographyImagesMax = settings?.blendedPhotographyImagesMax ?? 0;
  const takePhotography = settings?.takePhotography ?? '';
  const catalogOnlyMode = takePhotography === 'stock_photo_only' || takePhotography === 'not_needed';

  // Dropdown state
  const [selectedColorway, setSelectedColorway] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string>>({});

  // Colorway options: derived from variants, alphabetized
  const colorwayOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { code: string, display: string }[] = [];
    for (const v of variants) {
      if (!seen.has(v.colorCode)) {
        seen.add(v.colorCode);
        options.push({ code: v.colorCode, display: v.colorDisplay });
      }
    }
    return options.sort((a, b) => a.display.localeCompare(b.display));
  }, [variants]);

  // Auto-select first colorway on mount or when options change
  useEffect(() => {
    if (
      colorwayOptions.length > 0 &&
      !colorwayOptions.find(o => o.code === selectedColorway)
    ) {
      setSelectedColorway(colorwayOptions[0].code);
    }
  }, [colorwayOptions, selectedColorway]);

  // The variant matching the selected colorway
  const selectedVariant = useMemo(
    () => variants.find(v => v.colorCode === selectedColorway) ?? null,
    [variants, selectedColorway],
  );

  // Build the list of filterable dimensions from variant data
  const filterDimensions: FilterDimension[] = useMemo(() => {
    if (!selectedVariant) return [];
    const dims: FilterDimension[] = [
      {
        key: 'sizeDisplays',
        label: 'Size',
        allValues: selectedVariant.sizeDisplays,
      },
    ];
    if (selectedVariant.otherVitalElements) {
      for (const [key, values] of Object.entries(
        selectedVariant.otherVitalElements,
      )) {
        dims.push({ key, label: toLabel(key), allValues: values });
      }
    }
    return dims;
  }, [selectedVariant]);

  // Compute the available (deduplicated, sorted) values for a given dimension
  const getAvailableValues = useCallback(
    (dimKey: string): string[] => {
      if (!selectedVariant) return [];

      let indices = selectedVariant.skus.map((_, i) => i);

      for (const dim of filterDimensions) {
        if (dim.key === dimKey) continue;
        const filterVal = selectedFilters[dim.key];
        if (!filterVal || filterVal === ALL_VALUE) continue;
        indices = indices.filter(i => dim.allValues[i] === filterVal);
      }

      const targetDim = filterDimensions.find(d => d.key === dimKey);
      if (!targetDim) return [];

      const seen = new Set<string>();
      const result: string[] = [];
      for (const i of indices) {
        const val = targetDim.allValues[i];
        if (val && !seen.has(val)) {
          seen.add(val);
          result.push(val);
        }
      }
      return result.sort((a, b) => a.localeCompare(b));
    },
    [selectedVariant, filterDimensions, selectedFilters],
  );

  // Media state management (working copies, change handlers, eligible/ineligible slots)
  const {
    allImages,
    allStudioImages,
    allVideos,
    eligibleSlots,
    ineligibleAssets,
    handleImagesChange,
    handleStudioImagesChange,
    handleVideoChange,
    handleDeleteImage,
    handleUploadForKey,
    createBlobUrl,
  } = useMediaState({
    record,
    selectedVariant,
    selectedColorway,
    selectedFilters,
    filterDimensions,
    blendedPhotographyImages,
    catalogOnlyMode,
  });

  // Drag-and-drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [eligibleDragOverKey, setEligibleDragOverKey] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadDialogStep, setUploadDialogStep] = useState<'type' | 'label' | null>(null);
  const [selectedImageType, setSelectedImageType] = useState<'catalog' | 'studio'>('catalog');
  const [assetLabel, setAssetLabel] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Kick off the upload dialog flow when a file is dropped/selected
  const handleIneligibleFileSelected = useCallback((file: File) => {
    setPendingFile(file);
    if (isVideoFile(file) || catalogOnlyMode) {
      // Videos and catalog-only partners skip the type chooser
      setSelectedImageType('catalog');
      setUploadDialogStep('label');
    } else {
      setSelectedImageType('catalog');
      setUploadDialogStep('type');
    }
    setAssetLabel('');
  }, [catalogOnlyMode]);

  const handleUploadDialogCancel = useCallback(() => {
    setPendingFile(null);
    setUploadDialogStep(null);
    setAssetLabel('');
  }, []);

  const handleTypeChosen = useCallback(() => {
    setUploadDialogStep('label');
  }, []);

  const handleAddAsset = useCallback(async () => {
    if (!pendingFile || !assetLabel.trim() || !record) return;
    const key = assetLabel.trim().replace(/[^a-zA-Z0-9]/g, '_') || 'file';
    const isVideo = isVideoFile(pendingFile);

    if (isVideo) {
      // Upload video directly to S3 via pre-signed URL, then store the public URL
      setIsUploading(true);
      try {
        const token = requireAuth();
        const partner = requirePartner();
        const ext = pendingFile.name.split('.').pop() || 'mp4';
        const contentType = pendingFile.type || 'video/mp4';

        const { uploadUrl, publicUrl } = await getCatalogMediaUploadUrl({
          parentSku: record.id as string,
          partnerCode: partner.code,
          token,
          fileExtension: ext,
          contentType,
        });

        await uploadToS3({ presignedUrl: uploadUrl, file: pendingFile, contentType });

        const safeKey = nextAvailableName(key, allVideos);
        const updatedVideos = { ...allVideos, [safeKey]: publicUrl };
        handleVideoChange(updatedVideos);
      } catch (err) {
        console.error('Video upload failed:', err);
        const message = err instanceof Error ? err.message : 'Unknown error';
        notify(`Video upload failed: ${message}`, { type: 'error' });
      } finally {
        setIsUploading(false);
      }
    } else if (selectedImageType === 'catalog') {
      const safeKey = nextAvailableName(key, allImages);
      const blobUrl = createBlobUrl(pendingFile);
      const updatedImages = { ...allImages, [safeKey]: blobUrl };
      handleImagesChange(updatedImages, { [safeKey]: pendingFile });
    } else {
      const safeKey = nextAvailableName(key, allStudioImages);
      const blobUrl = createBlobUrl(pendingFile);
      const updatedImages = { ...allStudioImages, [safeKey]: blobUrl };
      handleStudioImagesChange(updatedImages, { [safeKey]: pendingFile });
    }

    setPendingFile(null);
    setUploadDialogStep(null);
    setAssetLabel('');
  }, [
    pendingFile, assetLabel, selectedImageType, record, allImages,
    allStudioImages, allVideos, handleImagesChange,
    handleStudioImagesChange, handleVideoChange, notify, createBlobUrl,
  ]);

  if (!record?.variants || !Array.isArray(record.variants)) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No variants available for media management.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', p: 2 }}>
      {/* Dropdown Filters Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Colorway</InputLabel>
          <Select
            value={selectedColorway}
            onChange={(e) => {
              setSelectedColorway(e.target.value);
              setSelectedFilters({});
            }}
            label="Colorway"
          >
            {colorwayOptions.map(opt => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.display}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {filterDimensions.map((dim) => {
          const availableValues = getAvailableValues(dim.key);
          const currentValue = selectedFilters[dim.key] ?? ALL_VALUE;
          return (
            <FormControl key={dim.key} size="small" sx={{ minWidth: 200 }}>
              <InputLabel>
                {dim.label}
                {' '}
                (optional)
              </InputLabel>
              <Select
                value={currentValue}
                onChange={e =>
                  setSelectedFilters(prev => ({
                    ...prev,
                    [dim.key]: e.target.value,
                  }))
                }
                label={`${dim.label} (optional)`}
              >
                <MenuItem value={ALL_VALUE}>
                  All
                  {' '}
                  {dim.label.toLowerCase()}
                  s
                </MenuItem>
                {availableValues.map(val => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
      </Box>

      {/* Eligible for Storefront Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            ELIGIBLE FOR STOREFRONT
          </Typography>
          {blendedPhotographyImagesMax > 0 && (
            <Chip
              label={`Max ${blendedPhotographyImagesMax} assets synced`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          The below assets are eligible to be synced to the storefront. Available assets will sync
          to the storefront in the order prescribed below up to the maximum number of assets
          indicated above. To adjust the maximum number of assets synced, please reference the
          Max Blended Photography Images partner settings in Trove Admin.
        </Alert>

        {eligibleSlots.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No eligible assets configured. Please configure blended photography settings in Trove
            Admin.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {eligibleSlots.map((slot, index) => (
              <Box key={slot.tag} sx={{ position: 'relative' }}>
                {/* Numbered chip */}
                <Chip
                  label={index + 1}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    zIndex: 2,
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />

                {slot.sourceType === 'inventory' ? (
                  /* Inventory-only slot (read-only) */
                  <Box
                    sx={{
                      width: 200,
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Inventory Item Image
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This asset is sourced directly from inventory item photography (where
                        available) and cannot be edited in this tool.
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slot.tag}
                      </Typography>
                    </Box>
                  </Box>
                ) : slot.url ? (
                  /* Catalog slot with image */
                  <Box sx={{ position: 'relative' }}>
                    <MediaAssetCard
                      imageKey={slot.tag}
                      url={slot.url}
                      source={slot.sourceType}
                      onDelete={slot.displayKey ? () => handleDeleteImage(slot.displayKey!, slot.sourceType as 'catalog' | 'studio' | 'video') : undefined}
                    />
                    {/* Diagonal split overlay for dual-source slots:
                        line from top-right to bottom-left; lower-right triangle greyed for inventory */}
                    {slot.hasInventorySource && (
                      <>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 50,
                            background: 'linear-gradient(to bottom right, transparent 49.5%, rgba(0,0,0,0.12) 50.5%)',
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 56,
                            right: 8,
                            fontSize: '0.6rem',
                            fontWeight: 'bold',
                            color: 'text.secondary',
                            letterSpacing: 0.5,
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}
                        >
                          INVENTORY
                        </Typography>
                      </>
                    )}
                    {/* Required badge */}
                    {slot.required && (
                      <Chip
                        label="REQUIRED"
                        size="small"
                        color="warning"
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          zIndex: 2,
                          fontSize: '0.6rem',
                          height: 20,
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  /* Empty catalog slot — upload placeholder with drag-and-drop */
                  <Box
                    role="button"
                    aria-label={`Upload file for ${slot.tag}`}
                    tabIndex={0}
                    onDragOver={(e) => { e.preventDefault(); setEligibleDragOverKey(slot.catalogKey); }}
                    onDragLeave={() => setEligibleDragOverKey(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setEligibleDragOverKey(null);
                      const file = e.dataTransfer.files[0];
                      if (file && slot.catalogKey) {
                        handleUploadForKey(slot.catalogKey, file, slot.uploadStorageType);
                      }
                    }}
                    component="label"
                    sx={{
                      width: 200,
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '2px dashed',
                      borderColor: eligibleDragOverKey === slot.catalogKey ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, background-color 0.2s',
                    }}
                  >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, bgcolor: eligibleDragOverKey === slot.catalogKey ? 'action.hover' : 'grey.50' }}>
                      <Chip
                        label={slot.required ? 'REQUIRED' : 'EMPTY'}
                        size="small"
                        color={slot.required ? 'error' : 'default'}
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Drag and drop files here
                      </Typography>
                      <Button variant="outlined" size="small" component="span">
                        Choose Files
                      </Button>
                    </Box>
                    <input
                      type="file"
                      hidden
                      accept="image/*,video/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && slot.catalogKey) {
                          handleUploadForKey(slot.catalogKey, file, slot.uploadStorageType);
                        }
                        e.target.value = '';
                      }}
                    />
                    <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slot.tag}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Ineligible for Storefront Section */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            INELIGIBLE FOR STOREFRONT
          </Typography>
          {ineligibleAssets.length > 0 && (
            <Chip
              label={`${ineligibleAssets.length} assets`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          These assets are stored in Trove&apos;s catalog but because they are added outside of partner
          configurations, are not eligible to be synced to the storefront. To modify these sync
          settings, please reference the Blended Photography Images partner settings in Trove Admin.
        </Alert>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {ineligibleAssets.map(asset => (
            <MediaAssetCard
              key={`${asset.source}-${asset.key}`}
              imageKey={asset.key}
              url={asset.url}
              source={asset.source}
              onDelete={() => handleDeleteImage(asset.key, asset.source as 'catalog' | 'studio' | 'video')}
            />
          ))}
          <Box
            role="button"
            aria-label="Upload new media asset"
            tabIndex={0}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              if (e.dataTransfer.files.length > 0) {
                handleIneligibleFileSelected(e.dataTransfer.files[0]);
              }
            }}
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: isDragOver ? 'primary.main' : 'divider',
              borderRadius: 1,
              textAlign: 'center',
              bgcolor: isDragOver ? 'action.hover' : 'grey.50',
              width: 200,
              height: 220,
              cursor: 'pointer',
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Drag and drop files here
            </Typography>
            <Button variant="outlined" size="small" component="span">
              Choose Files
            </Button>
            <input
              type="file"
              hidden
              accept="image/*,video/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleIneligibleFileSelected(file);
                }
                e.target.value = '';
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
          <Typography variant="caption" color="text.secondary">Catalog Image</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'secondary.dark' }} />
          <Typography variant="caption" color="text.secondary">Studio Image</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
          <Typography variant="caption" color="text.secondary">Video</Typography>
        </Box>
      </Box>

      {/* Step 1: Choose image type dialog */}
      <Dialog open={uploadDialogStep === 'type'} onClose={handleUploadDialogCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Choose image type</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            How should this image be categorized in the media gallery?
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              onClick={() => setSelectedImageType('catalog')}
              sx={{
                flex: 1, p: 2, border: '2px solid',
                borderColor: selectedImageType === 'catalog' ? 'primary.main' : 'divider',
                borderRadius: 1, cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.main' }} />
                <Typography fontWeight="bold">Catalog Image</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.5 }}>
                Product imagery sourced from the partner catalog feed.
              </Typography>
            </Box>
            <Box
              onClick={() => setSelectedImageType('studio')}
              sx={{
                flex: 1, p: 2, border: '2px solid',
                borderColor: selectedImageType === 'studio' ? 'secondary.dark' : 'divider',
                borderRadius: 1, cursor: 'pointer',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'secondary.dark' }} />
                <Typography fontWeight="bold">Studio Image</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 2.5 }}>
                Enhanced studio or lifestyle imagery produced in-house.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogCancel}>Cancel</Button>
          <Button onClick={handleTypeChosen} variant="contained">Next</Button>
        </DialogActions>
      </Dialog>

      {/* Step 2: Label this asset dialog */}
      <Dialog open={uploadDialogStep === 'label'} onClose={handleUploadDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Label this asset</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give this asset a short label to identify it in the Ineligible for Storefront section
            (e.g. &quot;lifestyle&quot;, &quot;swatch&quot;, &quot;packaging&quot;).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Enter a label"
            value={assetLabel}
            onChange={e => setAssetLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && assetLabel.trim() && !isUploading) {
                void handleAddAsset();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogCancel} disabled={isUploading}>Cancel</Button>
          <Button
            onClick={() => { void handleAddAsset(); }}
            variant="contained"
            disabled={!assetLabel.trim() || isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
          >
            {isUploading ? 'Uploading...' : 'Add asset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaTab;
